import React, { useState, useEffect, useRef } from "react";
import api from "frontend/js/api";

let typeOptions = [];
let serviceOptions = [];

interface StoreFilterProps {
  selectedTypes: string[];
  selectedServices: string[];
  onChangeTypes: (values: string[]) => void;
  onChangeServices: (values: string[]) => void;
}

const StoreFilter = ({
  selectedTypes,
  selectedServices,
  onChangeTypes,
  onChangeServices
}: StoreFilterProps) => {
  useEffect(() => {
    const query = `
    {
      storeServices: metaobjects(type: "store_services", first: 50) {
        edges {
          node {
            id
            handle
            service_label: field(key: "service_label") { value }
          }
        }
      }
      storeTypes: metaobjects(type: "store_types", first: 50) {
        edges {
          node {
            id
            handle
            title: field(key: "title") { value }
          }
        }
      }
    }
    `;

    fetch('/api/2024-07/graphql.json', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': api.getStorefrontToken(),
      },
      body: JSON.stringify({ query })
    })
    .then(res => res.json())
    .then(data => {
      serviceOptions = data.data.storeServices.edges.map(edge => {
        const v = edge.node.service_label.value;
        return { value: v, label: v };
      });

      typeOptions = data.data.storeTypes.edges.map(edge => {
        const v = edge.node.title.value;
        return { value: v, label: v };
      });
    });
  }, []);

  const [openTypes, setOpenTypes] = useState(false);
  const [openServices, setOpenServices] = useState(false);

  const typesRef = useRef<HTMLDivElement>(null);
  const servicesRef = useRef<HTMLDivElement>(null);

  const toggleTypes = () => setOpenTypes(!openTypes);
  const toggleServices = () => setOpenServices(!openServices);

  const handleTypesCheck = (type: string) => {
    const newValues = selectedTypes.includes(type)
      ? selectedTypes.filter((t) => t !== type)
      : [...selectedTypes, type];

    onChangeTypes(newValues);
  };

  const handleServicesCheck = (service: string) => {
    const newValues = selectedServices.includes(service)
      ? selectedServices.filter((s) => s !== service)
      : [...selectedServices, service];

    onChangeServices(newValues);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (typesRef.current && !typesRef.current.contains(event.target as Node)) {
        setOpenTypes(false);
      }

      if (servicesRef.current && !servicesRef.current.contains(event.target as Node)) {
        setOpenServices(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="store-filter flex gap-4 relative justify-center flex-col lg:flex-row">
      {/* STORE TYPES DROPDOWN */}
      <div className="relative lg:w-1/2 w-full" ref={typesRef}>
        <button onClick={toggleTypes} className="filter-btn flex justify-between w-full border">
          <span>Store Types</span>
          <span className={`transition-transform ${openTypes ? "rotate-180" : ""}`}>
            <svg class="w-4 h-4 transition-transform " viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z" clip-rule="evenodd"></path></svg>
          </span>
        </button>

        <div className={`dropdown absolute bg-white border shadow p-3 mt-2 w-full z-10 overflow-auto max-h-[300px] ${openTypes ? "block" : "hidden"}`}>
          {typeOptions.map((type: any) => {
            return (
              <label key={type.value} className="flex gap-2 items-center py-1 cursor-pointer">
                <input
                  id={`store-type-${type.value}`}
                  name="store-type"
                  type="checkbox"
                  checked={selectedTypes.includes(type.value)}
                  onChange={() => handleTypesCheck(type.value)}
                  style={selectedTypes.includes(type.value) ? { backgroundColor: '#00263E', border: 'none', borderRadius: '3px' } : {}}
                />
                {type.label}
              </label>
            );
          })}
        </div>
      </div>

      {/* SERVICES DROPDOWN  */}
      <div className="relative lg:w-1/2 w-full" ref={servicesRef}>
        <button onClick={toggleServices} className="filter-btn flex justify-between w-full border">
          <span>Services</span>
          <span className={`transition-transform ${openServices ? "rotate-180" : ""}`}>
            <svg class="w-4 h-4 transition-transform " viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z" clip-rule="evenodd"></path></svg>
          </span>
        </button>

        <div className={`dropdown absolute bg-white border shadow p-3 mt-2 w-full z-10 overflow-auto max-h-[300px] ${openServices ? "block" : "hidden"}`}>
          {serviceOptions.map((service: any) => {
            return (
              <label key={service.value} className="flex gap-2 items-center py-1 cursor-pointer">
                <input
                  id={`store-service-${service.value}`}
                  name="store-service"
                  type="checkbox"
                  checked={selectedServices.includes(service.value)}
                  onChange={() => handleServicesCheck(service.value)}
                  style={selectedServices.includes(service.value) ? { backgroundColor: '#00263E', border: 'none', borderRadius: '3px' } : {}}
                />
                {service.label}
              </label>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default StoreFilter;
